// src/pages/setTextReaderGate.js
//
// Pure, framework-free decision helpers for the set-text reader's paid
// (entitlement-gated) section (Slice 2). Side-effect-free so the gate decision and
// the item-type filter are unit-testable without React/Firestore. Consumed by
// src/pages/PackReaderSetText.jsx.
import { byDisplayOrder } from './readerGrouping.js';
import { orderWorkshopGuides } from './guidePractice.js';
//
// Gating contract (Firestore rules enforce the server half at firestore.rules:114-119;
// this is the client half):
//   - free set_text texts ALWAYS render (never blocked on entitlement);
//   - paid set_text texts load + render ONLY when the user has an ACTIVE entitlement
//     for the pack. Strict-gate: when not entitled we do not even attempt the paid
//     read (see shouldFetchPaidItems) -- it is gated, not attempt-then-deny.

// D4: the reader's text list renders ONLY set_text items. Audio and any other item
// type are filtered out, in BOTH the free and paid scopes.
export function selectSetTextItems(items) {
    return (items || []).filter((it) => it && it.type === 'set_text');
}

// Slice 3: the audio items from a scope (podcasts). Same gating as everything else --
// callers pass freeItems (always) and paidItems (only loaded when entitled, Slice 2),
// so a non-entitled session never receives paid audio here.
export function selectAudioItems(items) {
    return (items || []).filter((it) => it && it.type === 'audio');
}

// Slice 3 routing (S1): split audio into per-text (linked via content.relatedTextId to a
// text the user can currently SEE) and pack-level (no link, or a link to a text that is
// absent/locked for this user). `knownTextIds` is the set of currently-visible set_text
// ids (free always + paid when entitled). Returns:
//   { byText: { [textId]: [audio, ...] }, packLevel: [audio, ...] }
// Nothing is dropped -- an unmatched link falls through to packLevel.
export function partitionAudioByText(audioItems, knownTextIds) {
    const known = knownTextIds instanceof Set ? knownTextIds : new Set(knownTextIds || []);
    const byText = {};
    const packLevel = [];
    for (const a of audioItems || []) {
        if (!a) continue;
        const rel = a.content && a.content.relatedTextId;
        if (rel && known.has(rel)) {
            (byText[rel] || (byText[rel] = [])).push(a);
        } else {
            packLevel.push(a);
        }
    }
    return { byText, packLevel };
}

// Per-text PRACTICE grouping (mirrors partitionAudioByText). Buckets each practice item by
// its text link when that text is currently visible; unmatched / unlinked items fall to a
// pack-level ("All texts") bucket (nothing dropped). Reads the link from content
// (raw flashcard / essay_prompt items) OR the item root (mapped quiz questions out of
// toQuizQuestions), so one partition serves all three practice pools.
export function partitionPracticeByText(items, knownTextIds) {
    const known = knownTextIds instanceof Set ? knownTextIds : new Set(knownTextIds || []);
    const byText = {};
    const packLevel = [];
    for (const it of items || []) {
        if (!it) continue;
        const rel = (it.content && it.content.relatedTextId) || it.relatedTextId || null;
        if (rel && known.has(rel)) {
            (byText[rel] || (byText[rel] = [])).push(it);
        } else {
            packLevel.push(it);
        }
    }
    return { byText, packLevel };
}

// The practice items to show for a selected text: that text's items + the pack-level
// ("All texts") items (mirrors how audio renders per-text + pack-level together). A null
// textId means the caller wants the FULL pool ("All texts" view) and should not call this.
export function practiceForText(partition, textId) {
    const p = partition || { byText: {}, packLevel: [] };
    return [...((p.byText && p.byText[textId]) || []), ...(p.packLevel || [])];
}

// Per-section "Drill it" core (skills-section-drills + skills-section-flashcards):
// the practice items to drill INSIDE one section's view -- that section's OWN linked
// bucket ONLY. The pack-level bucket is deliberately EXCLUDED (unlike
// practiceForText): pack-level items already have a home in the pooled Practice nav,
// and duplicating the whole pool under every section would drown the per-lesson
// drill. An item linked to a section the user cannot currently SEE (e.g. paid + not
// entitled) partitions to pack-level and so stays out of every drill (and out of
// paid leakage) by the same knownItemIds gate as audio. null/undefined sectionId -> [].
function ownDrillBucket(items, knownItemIds, sectionId) {
    if (!sectionId) return [];
    const { byText } = partitionPracticeByText(items, knownItemIds);
    return byText[sectionId] || [];
}

// The quizzes to drill inside one section's view (slice 1).
export function drillQuizzesForSection(quizItems, knownItemIds, sectionId) {
    return ownDrillBucket(quizItems, knownItemIds, sectionId);
}

// The flashcards to revise inside one section's view (slice 1b -- the admin's
// "Linked text" selector already lets an author link a flashcard to a section;
// this is the matching reader bucket). Same own-bucket-only semantics as quizzes.
export function drillFlashcardsForSection(flashcardItems, knownItemIds, sectionId) {
    return ownDrillBucket(flashcardItems, knownItemIds, sectionId);
}

// ---- Flashcards (play view) ----

// The flashcard items from a scope. Same gating as everything else: callers pass
// freeItems (always) and paidItems (only loaded when entitled), so a non-entitled
// session never receives paid card content.
export function selectFlashcards(items) {
    return (items || []).filter((it) => it && it.type === 'flashcard');
}

// Free/paid split (mirrors partitionAudioByText). free = always playable; paid = only
// when the caller's paidItems is populated (i.e. entitled -- the Slice 2 gate already
// leaves paidItems empty for non-entitled users).
export function partitionFlashcards(freeItems, paidItems) {
    return { free: selectFlashcards(freeItems), paid: selectFlashcards(paidItems) };
}

// The deck a user actually plays: free cards always; paid cards only when entitled.
// (Structural gate -- paid is excluded unless entitled even if a caller mistakenly
// passes populated paid cards.)
export function flashcardDeck({ free, paid, entitled }) {
    return entitled ? [...(free || []), ...(paid || [])] : [...(free || [])];
}

// Essay guides from already-authorised items, numbering workshop series within stored guide slots.
// The set-text reader exposes all guides. The singular compatibility wrapper
// below still returns the first guide or null for other existing consumers.
export function selectGuideSections(items) {
    return orderWorkshopGuides(byDisplayOrder((items || []).filter((it) =>
        it && it.type === 'lit_section' && it.content && it.content.sectionKind === 'guide')));
}

// Compatibility for single-guide consumers; the set-text reader uses the full list.
export function selectGuideSection(items) {
    return selectGuideSections(items)[0] || null;
}

// The Essays view's guide-vs-prompt selection (essay-structure-guide). Given the
// CURRENTLY VISIBLE essays (the practice filter may have narrowed them), the pack
// guide (or null) and the active selection key, decide which entry renders:
//   onGuide -- render the guide. True when the guide is explicitly selected OR there
//     is no essay to show but a guide EXISTS. That second clause keeps the always-on
//     guide reachable even when the "Practice for" filter empties the essay list
//     under a STALE essay selKey (found by the adversarial review).
//   sel     -- the essay to render when NOT on the guide (matching essay, else the
//     first; null only when there are no essays -- in which case onGuide is true if a
//     guide exists, else the caller shows the empty-state note).
export function resolveEssayEntry(essays, guide, activeKey) {
    const list = Array.isArray(essays) ? essays : [];
    const sel = list.find((e) => e && e.id === activeKey) || list[0] || null;
    const onGuide = !!guide && (activeKey === (guide && guide.id) || !sel);
    return { onGuide, sel };
}

// Whether to show the generic "unlock more cards" CTA: only for a RESOLVED non-entitled
// user who already has at least one (free) card to play. Never while entitlement is
// resolving (don't flash locked to an entitled user), and never with an empty deck
// (F5: zero flashcards -> empty state, no stray CTA).
export function showFlashcardLockedCta({ entLoading, entitled, deckCount }) {
    return !entLoading && !entitled && deckCount > 0;
}

// ---- Quizzes (MCQ play view) ----

// The quiz items from a scope (same gating: free always; paid only when entitled, since
// paidItems is empty for non-entitled users -- no paid quiz content can leak).
export function selectQuizzes(items) {
    return (items || []).filter((it) => it && it.type === 'quiz');
}

// Free/paid split (mirrors partitionFlashcards).
export function partitionQuizzes(freeItems, paidItems) {
    return { free: selectQuizzes(freeItems), paid: selectQuizzes(paidItems) };
}

// L2b: essay_prompt items from a scope (the reader Practice section's timed-essay
// activities). Same gating: free always; paid only when entitled (paidItems is
// empty for non-entitled users, so no paid prompt can leak).
export function selectEssayPrompts(items) {
    return (items || []).filter((it) => it && it.type === 'essay_prompt');
}

// The quiz pool a user actually plays: free always; paid only when entitled. (Structural
// gate -- paid excluded unless entitled even if a caller passes populated paid items.)
export function quizDeck({ free, paid, entitled }) {
    return entitled ? [...(free || []), ...(paid || [])] : [...(free || [])];
}

// Generic "unlock more quizzes" CTA gate -- resolved + not entitled + a non-empty pool.
export function showQuizLockedCta({ entLoading, entitled, deckCount }) {
    return !entLoading && !entitled && deckCount > 0;
}

// Map stored quiz items to runnable questions, SKIPPING malformed ones (Q6) rather
// than crashing the runner. Each output carries a `type` discriminator the shared
// QuizQuestion renderer + the isQuestionCorrect scorer key off:
//   mcq      -> { type:'mcq', question, options(>=2), correct(int in range), hint, explanation }
//   fill     -> { type:'fill', question, answers(>=1 non-empty), hint, explanation }
//   matching -> { type:'matching', question, pairs(>=2 {left,right}), explanation }
// `questionType` 'fill_in'/'fill' both map to 'fill'. Accepts the `question` alias.
export function toQuizQuestions(items) {
    const out = [];
    for (const it of items || []) {
        const c = (it && it.content) || {};
        const question = c.questionText || c.question;
        if (!question) continue;
        const qt = c.questionType || 'mcq';
        const hint = (Array.isArray(c.hints) && c.hints[0]) || null;
        const explanation = c.explanation || null;
        // Carry the per-text grouping link through (was dropped before) so the reader can
        // bucket mapped questions by text -- see partitionPracticeByText.
        const relatedTextId = c.relatedTextId || null;

        if (qt === 'fill' || qt === 'fill_in') {
            const answers = (Array.isArray(c.answers) ? c.answers : (c.answer != null ? [c.answer] : []))
                .map((a) => String(a == null ? '' : a)).filter((s) => s.trim().length > 0);
            if (!answers.length) continue;
            out.push({ id: it.id, type: 'fill', question, answers, hint, explanation, relatedTextId });
        } else if (qt === 'matching') {
            const pairs = (Array.isArray(c.pairs) ? c.pairs : [])
                .filter((p) => p && typeof p === 'object' && p.left && p.right)
                .map((p) => ({ left: String(p.left), right: String(p.right) }));
            if (pairs.length < 2) continue;
            out.push({ id: it.id, type: 'matching', question, pairs, explanation, relatedTextId });
        } else {
            // mcq (default). Options preserved (never reordered) so `correct` stays valid.
            const options = Array.isArray(c.options) ? c.options : null;
            const correct = c.correctAnswer;
            if (!options || options.length < 2) continue;
            if (!Number.isInteger(correct) || correct < 0 || correct >= options.length) continue;
            out.push({ id: it.id, type: 'mcq', question, options, correct, hint, explanation, relatedTextId });
        }
    }
    return out;
}

// D3 strict-gate: only fetch /paidItems once entitlement has RESOLVED and is ACTIVE.
// While entitlement is still resolving (entLoading), returns false so no read is
// attempted; the UI shows a loading state rather than "locked".
export function shouldFetchPaidItems({ entLoading, entitled }) {
    return !entLoading && !!entitled;
}

// D2 state machine for the paid section of the sidebar. Drives whether the reader
// shows a spinner, the generic locked CTA, an inline error, an empty notice, or the
// paid texts. Never returns 'locked' while entitlement is still resolving (so an
// entitled user never flashes "locked" mid-resolve), and never returns 'empty'/'ready'
// before the paid read has settled (so it never flashes empty before loading).
//
//   'resolving' -> entitlement not yet known (entLoading)
//   'locked'    -> resolved, not entitled (generic buy-to-unlock CTA, D1)
//   'error'     -> entitled, the paidItems read threw (D5 inline error; free read intact)
//   'loading'   -> entitled, paidItems read in flight or not yet settled
//   'empty'     -> entitled, read settled OK, zero paid set_text items (D4 graceful)
//   'ready'     -> entitled, one or more paid set_text texts to show
export function paidSectionState({ entLoading, entitled, paidSettled, paidLoading, paidError, paidCount }) {
    if (entLoading) return 'resolving';
    if (!entitled) return 'locked';
    if (paidError) return 'error';
    if (paidLoading || !paidSettled) return 'loading';
    if (!paidCount) return 'empty';
    return 'ready';
}
